import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck, CheckCircle2, GitBranch, UserCog, Clock, AlertTriangle, MessageSquare, CalendarRange, Paperclip, AtSign } from "lucide-react";
import { timeAgo } from "@/lib/helpers";

const ICONS = {
  assigned: UserCog,
  handoff: GitBranch,
  reassigned: GitBranch,
  status_changed: CheckCircle2,
  due_changed: CalendarRange,
  due_soon: Clock,
  overdue: AlertTriangle,
  comment: MessageSquare,
  mention: AtSign,
  attachment_added: Paperclip,
  weekly: CalendarRange,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const ref = useRef();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count); } catch (e) {}
  }, []);
  const loadItems = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch (e) {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n) loadItems(); };

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    setOpen(false);
    loadCount();
    if (n.type === "weekly") navigate("/overview");
    else navigate("/tasks");
  };

  const markAll = async () => { await api.post("/notifications/read-all"); loadItems(); loadCount(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative flex h-9 w-9 items-center justify-center border border-zinc-200 text-zinc-600 transition-colors hover:border-[#b32233] hover:text-[#b32233]">
        <Bell className="h-4 w-4" />
        {count > 0 && <span data-testid="notification-count" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center bg-[#b32233] px-1 text-[10px] font-bold text-white">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 top-11 z-50 w-80 border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notifications</span>
            <button onClick={markAll} data-testid="mark-all-read" className="flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline"><CheckCheck className="h-3 w-3" /> Mark all read</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="px-3 py-6 text-center text-sm text-zinc-400">No notifications</p>}
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`} className={`flex w-full items-start gap-2.5 border-b border-zinc-100 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 ${n.read ? "" : "bg-[#fdf0f1]/40"}`}>
                  <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border ${n.read ? "border-zinc-200 text-zinc-400" : "border-[#b32233] text-[#b32233]"}`}><Icon className="h-3 w-3" /></div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${n.read ? "text-zinc-600" : "font-semibold text-zinc-900"}`}>{n.title}</p>
                    <p className="mt-0.5 text-xs leading-snug text-zinc-500">{n.message}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-400">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-[#b32233]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
